import { SCREENS } from "@/config/screens";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Row, Table } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { memo, useContext, useEffect } from "react";
import { LoadingSpinner } from "../loading.spinner";
import { ScrollContainerRefContext } from "./scroll.container.ref.context";
import { TanstackTableRow } from "./tanstack.table.row";

interface TanstackTableBodyProps<T extends object, K extends object> {
  table: Table<T | K>;
  columnOrder: string[];
}
const ROW_HEIGHT = { base: 60, "2xl": 80 } as const;
//TODO: Переписать тело таблицы и навигацию нормально
export const TanstackTableBody = <T extends object, K extends object>({
  table,
  columnOrder,
}: TanstackTableBodyProps<T, K>) => {
  const scrollRef = useContext(ScrollContainerRefContext);
  const is2xl = useMediaQuery(`(min-width: ${SCREENS["2xl"]})`);
  const rowHeight = is2xl ? ROW_HEIGHT["2xl"] : ROW_HEIGHT.base;
  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => {
      if (!scrollRef || !scrollRef.current) {
        return null;
      }
      return scrollRef.current;
    },
    estimateSize: () => rowHeight,
    measureElement:
      typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowHeight, rowVirtualizer]);
  return (
    <div
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: "relative",
      }}
    >
      {!scrollRef || (!scrollRef.current && <LoadingSpinner />)}
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = table.getRowModel().rows[virtualRow.index] as Row<T | K>;
        return (
          <TanstackTableRow
            key={row.id}
            columnOrder={columnOrder}
            row={row}
            virtualRow={virtualRow}
            rowVirtualizer={rowVirtualizer}
            height={rowHeight}
          />
        );
      })}
    </div>
  );
};

export const MemoizedTanstackTableBody = memo(
  TanstackTableBody,
  (prev, next) => prev.table.options.data === next.table.options.data
) as typeof TanstackTableBody;
